import { cancelFunctions } from 'utils/cancelFunctions';
import { getSolutionSummary } from 'modules/api/directSalesCampaign';
import { useDispatch, useSelector } from 'react-redux';
import { Store } from 'components/common/types/Store.types';
import { getIsAnyLinePricedWithTransientStates } from 'utils/getIsAnyLinePriced';
import {
  changeFormParams,
  clearAssets,
  clearDirectSalesAssetList,
  clearDirectSalesPrices,
  setDirectSalesAssetList,
  setDirectSalesGrid,
  setDirectSalesPrices,
} from 'store/dealManagement/reducer';
import { DirectSalesLine, DirectSalesPricingSolutionResponse } from 'components/common/types/DirectSalesCampaign.types';
import { transformAssetList } from './utils/transformAssetList';
import transformDirectSalesPrice from './utils/transformDirectSalesPrice';
import { isCampaignCancelled } from './utils/isCampaignCancelled';
import { isSomeDirectSalesStatusFailed } from './utils/directSalesFailedStatus';

type Args = {
  campaignId: string;
  marketId: string;
  pricingSolution: DirectSalesPricingSolutionResponse | null;
  lines: Partial<DirectSalesLine>[];
};

type UseDirectSalesPricedSolution = (args: Args) => Promise<void>;

export const useDirectSalesPricedSolution = (): UseDirectSalesPricedSolution => {
  const dispatch = useDispatch();
  const transientLineStates = useSelector((state: Store) => state.dealManagement.directSales.transientLineStates);

  return async ({ campaignId, marketId, pricingSolution, lines }: Args): Promise<void> => {
    if (pricingSolution) dispatch(setDirectSalesGrid(pricingSolution.grid));

    const isSomeLinePriced = getIsAnyLinePricedWithTransientStates(lines, transientLineStates);

    if (pricingSolution && isSomeLinePriced && !isCampaignCancelled(lines)) {
      try {
        dispatch(changeFormParams({ isEditingDisabled: true, isLoading: true }));
        const data = await getSolutionSummary(campaignId, marketId, cancelFunctions);

        dispatch(setDirectSalesPrices(transformDirectSalesPrice(pricingSolution)));
        dispatch(setDirectSalesAssetList(transformAssetList(data)));
        dispatch(changeFormParams({ isEditingDisabled: false, isLoading: false }));
        // eslint-disable-next-line no-empty
      } catch {
        dispatch(changeFormParams({ isEditingDisabled: false, isLoading: false }));
      }
    } else if (!isSomeDirectSalesStatusFailed(lines as DirectSalesLine[])) {
      dispatch(clearDirectSalesAssetList());
      dispatch(clearAssets());
      dispatch(clearDirectSalesPrices());
    }
  };
};
